import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * usePagination — slices an array into pages.
 *
 *  const { page, setPage, pageSize, setPageSize, paged, total, pageCount } =
 *    usePagination(list, 50);
 *  ...render paged...
 *  <Pagination page={page} setPage={setPage} pageSize={pageSize}
 *    setPageSize={setPageSize} total={total} pageCount={pageCount}
 *    testidPrefix="opd" />
 */
export function usePagination(list, defaultPageSize = 25) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const total = Array.isArray(list) ? list.length : 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(() => {
    if (!Array.isArray(list)) return [];
    const start = (safePage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, safePage, pageSize]);

  return {
    page: safePage,
    setPage: (n) => setPage(Math.max(1, Math.min(n, pageCount))),
    pageSize,
    setPageSize: (n) => { setPageSize(n); setPage(1); },
    paged,
    total,
    pageCount,
  };
}

const SIZES = [25, 50, 100, 200];

export function Pagination({ page, setPage, pageSize, setPageSize, total, pageCount, testidPrefix = "pg", pageSizeDisabled = true }) {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 border-t border-[var(--hms-border-2)] bg-[var(--hms-paper)] text-xs"
      data-testid={`${testidPrefix}-pagination`}
    >
      <div className="text-[var(--hms-text-2)] font-medium" data-testid={`${testidPrefix}-pagination-info`}>
        Showing <span className="font-bold text-[var(--hms-ink)]">{from}</span>–<span className="font-bold text-[var(--hms-ink)]">{to}</span> of <span className="font-bold text-[var(--hms-ink)]">{total}</span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => setPage(page - 1)} disabled={page <= 1}
          data-testid={`${testidPrefix}-pagination-prev`}
          className="h-7 w-7 p-0 rounded-sm border-[var(--hms-border-2)] bg-white"
          title="Previous page"
        >
          <CaretLeft size={12} weight="bold" />
        </Button>
        <span className="px-2 font-mono text-[11px] text-[var(--hms-text-2)] min-w-[56px] text-center" data-testid={`${testidPrefix}-pagination-page`}>
          {page} / {pageCount}
        </span>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => setPage(page + 1)} disabled={page >= pageCount}
          data-testid={`${testidPrefix}-pagination-next`}
          className="h-7 w-7 p-0 rounded-sm border-[var(--hms-border-2)] bg-white"
          title="Next page"
        >
          <CaretRight size={12} weight="bold" />
        </Button>
      </div>
    </div>
  );
}
